/* ============================================================
   Primary navigation.

   What was here before: an unlabelled (i) and a brand switcher. The
   switcher is a white-label control — useful to a partner running the
   app under their own mark, meaningless to the freight operator the
   site is actually for — and it occupied the most valuable space on the
   page. Fourteen live tools had no route into them except going home
   first and scrolling.

   So the top right is now real navigation, built from the same TOOLS
   registry the homepage and the router read. A tool cannot appear in
   this menu without a route, and only live tools appear at all.

     Move    the eleven operational diagnostics
     Build   the greenfield tools
     Learn   method, FAQ, glossary, the compliance calendar
     Saved   direct, with a live count when there is anything to see

   Under 900px all four collapse into one labelled Menu — a labelled
   control, not a bare hamburger, because "Menu" is unambiguous and the
   header has room for the word.

   The brand switcher survives at the foot of the menu under its own
   heading, and stays in the header only while a white-label brand is
   actually active, so a partner can always get back out of it.

   Audit A-12: setBrand() re-renders through the `brandchange` event.
   ============================================================ */

import { getBrand, getBrandList, setBrand, isWhiteLabel } from '../shared/brand.js';
import { TOOLS } from '../data/tools.js';
import { idb } from '../shared/storage.js';
import { icon } from '../shared/icons.js';
import { html, raw } from '../shared/html.js';
import { router } from '../shared/router.js';
import { Logo } from './Logo.js';

/* Live only. A planned tool renders an honest roadmap state, which is
   the right thing to find at the end of a link someone followed from
   the homepage — and the wrong thing to offer in primary navigation,
   where every entry is a promise that something is there. Saved work is
   its own destination rather than a member of either track. */
const inTrack = (track) =>
  TOOLS.filter((t) => t.track === track && t.status === 'live' && t.id !== 'saved');

const LEARN = [
  { path: '/about-us', title: 'About us', summary: 'Who runs this practice, its standing, and how to reach it' },
  { path: '/about', title: 'Method & sources', summary: 'How every figure is arrived at, and its limits' },
  { path: '/faq', title: 'FAQ', summary: 'Straight answers, including the awkward ones' },
  { path: '/glossary', title: 'Regulatory Glossary', summary: 'The vocabulary, and what goes wrong around it' },
  { path: '/compliance', title: 'Compliance Calendar', summary: 'Every instrument, with live status' }
];

const SECTIONS = [
  { id: 'move', label: 'Move', caption: 'Operational diagnostics', items: inTrack('move') },
  { id: 'build', label: 'Build', caption: 'Greenfield navigation', items: inTrack('build') },
  { id: 'learn', label: 'Learn', caption: 'The ground under the numbers', items: LEARN }
];

let openMenu = null;
let savedCount = null;

/** Count without blocking the first paint; the badge fills in after. */
async function readSavedCount() {
  try {
    const [scorecards, calculations] = await Promise.all([
      idb.count('scorecards'),
      idb.count('benchmarks')
    ]);
    return scorecards + calculations;
  } catch {
    return 0; // no IndexedDB (private mode, old browser) — show no badge
  }
}

export function Header() {
  const nav = document.createElement('nav');
  nav.className = 'nav-bar';
  nav.setAttribute('aria-label', 'Primary');

  const paint = () => {
    closeMenu();
    const brand = getBrand();
    const here = router.path;

    // On a tool screen the logo alone is not an obvious way back — it
    // reads as branding, not navigation. Show an explicit affordance.
    const atHome = here === '/';

    const sectionButton = (s) => {
      const active = s.items.some((i) => i.path === here);
      return html`
        <button
          class="nav-link"
          data-menu="${s.id}"
          aria-haspopup="true"
          aria-expanded="false"
          aria-current="${active ? 'true' : 'false'}"
        >
          <span>${s.label}</span>${raw(icon('down', 13))}
        </button>
      `;
    };

    const badge =
      savedCount > 0 ? html`<span class="nav-badge">${String(savedCount)}</span>` : '';

    nav.innerHTML = html`
      <div class="row" style="gap: var(--ca-space-2); min-width: 0;">
        ${atHome
          ? ''
          : html`<button class="btn btn-ghost btn-sm" id="nav-back" aria-label="Back to the dashboard">
              ${raw(icon('left', 17))}
            </button>`}
        <!-- The accessible name has to contain the visible text, in order
             (WCAG 2.5.3). The lockup renders the short name AND the
             descriptor, so "Kanda — home" left "Logistics Advisory"
             visible on screen and absent from the name a speech-control
             user would say to activate it. -->
        <a
          href="/"
          class="nav-logo"
          id="nav-logo"
          aria-label="${brand.shortName}${brand.descriptor ? ` ${brand.descriptor}` : ''} — home"
        ></a>
      </div>

      <div class="nav-right">
        ${isWhiteLabel() ? html`<span class="nav-meta">Powered by Kanda</span>` : ''}

        <div class="nav-links">
          ${SECTIONS.map(sectionButton)}
          <a
            href="/saved"
            class="nav-link"
            aria-current="${here === '/saved' ? 'page' : 'false'}"
          >
            <span>Saved</span>${badge}
          </a>
        </div>

        ${isWhiteLabel()
          ? html`<button class="btn btn-ghost btn-sm nav-brand-exit" data-menu="brand"
                   aria-haspopup="true" aria-expanded="false" aria-label="Switch brand">
              ${raw(icon('globe', 15))}
            </button>`
          : ''}

        <button
          class="btn btn-secondary btn-sm nav-menu-toggle"
          data-menu="all"
          aria-haspopup="true"
          aria-expanded="false"
        >
          ${raw(icon('menu', 15))}<span>Menu</span>
        </button>
      </div>
    `;

    nav.querySelector('#nav-logo').appendChild(
      Logo({
        height: 17,
        name: brand.shortName,
        descriptor: brand.descriptor,
        showDescriptor: Boolean(brand.descriptor)
      })
    );

    nav.querySelector('#nav-back')?.addEventListener('click', () => router.navigate('/'));

    nav.querySelectorAll('[data-menu]').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.stopPropagation();
        toggleMenu(btn, btn.dataset.menu);
      });
    });
  };

  paint();

  if (savedCount === null) {
    readSavedCount().then((n) => {
      savedCount = n;
      if (n > 0) paint();
    });
  }

  window.addEventListener('brandchange', paint);
  window.addEventListener('routechange', () => {
    // A save or a delete only changes the count on the way out of a tool.
    readSavedCount().then((n) => {
      savedCount = n;
      paint();
    });
  });

  return nav;
}

/* ---------------- menu ---------------- */

function itemMarkup(item, here) {
  return html`
    <a
      href="${item.path}"
      class="nav-item"
      role="menuitem"
      aria-current="${item.path === here ? 'page' : 'false'}"
    >
      <span class="nav-item-title">${item.title}</span>
      <span class="nav-item-summary">${item.summary}</span>
    </a>
  `;
}

function sectionMarkup(section, here) {
  return html`
    <div class="nav-group">
      <p class="nav-group-head">
        <span>${section.label}</span>
        <span class="nav-group-caption">${section.caption}</span>
      </p>
      ${section.items.map((i) => itemMarkup(i, here))}
    </div>
  `;
}

function brandMarkup() {
  const currentKey = getBrand().key;
  return html`
    <div class="nav-group nav-group-brand">
      <p class="nav-group-head">
        <span>Partner view</span>
        <span class="nav-group-caption">Run this suite under your own mark</span>
      </p>
      ${getBrandList().map(
        (b) => html`
          <button
            class="nav-item"
            role="menuitem"
            data-brand="${b.key}"
            aria-current="${b.key === currentKey ? 'true' : 'false'}"
          >
            <span class="nav-item-title">${b.name}</span>
          </button>
        `
      )}
    </div>
  `;
}

/**
 * @param {HTMLElement} anchor
 * @param {string} which  a section id, 'brand', or 'all' for the full panel
 */
function toggleMenu(anchor, which) {
  if (openMenu?.which === which) {
    closeMenu();
    return;
  }
  closeMenu();

  const here = router.path;
  const section = SECTIONS.find((s) => s.id === which);
  const panel = document.createElement('div');
  panel.className = 'nav-panel';
  if (which === 'all') panel.classList.add('nav-panel-full');
  // Move is ten tools: one column runs past the fold on a laptop.
  else if (section && section.items.length > 6) panel.classList.add('nav-panel-wide');
  panel.setAttribute('role', 'menu');

  if (which === 'all') {
    panel.innerHTML = html`
      ${SECTIONS.map((s) => sectionMarkup(s, here))}
      <div class="nav-group">
        <p class="nav-group-head"><span>Your work</span></p>
        ${itemMarkup(
          { path: '/saved', title: 'Saved work', summary: 'Everything kept on this device' },
          here
        )}
      </div>
      ${brandMarkup()}
    `;
  } else if (which === 'brand') {
    panel.innerHTML = brandMarkup();
  } else {
    panel.innerHTML = sectionMarkup(section, here);
  }

  document.body.appendChild(panel);
  position(panel, anchor);
  anchor.setAttribute('aria-expanded', 'true');
  openMenu = { panel, anchor, which };

  panel.querySelectorAll('[data-brand]').forEach((btn) => {
    btn.addEventListener('click', () => {
      setBrand(btn.dataset.brand);
      closeMenu();
    });
  });

  // Links close the panel themselves; the router's delegated handler
  // does the navigating, so this only tidies up after it.
  panel.querySelectorAll('a').forEach((a) => a.addEventListener('click', closeMenu));

  // Both bound to the document, not the panel: after a click focus is
  // still on the button, which is outside the panel, so a panel-scoped
  // handler never sees the first ArrowDown — the one that is supposed
  // to get you in.
  //
  // Keydown attaches NOW. It was inside the setTimeout below and that
  // left a window where the panel was on screen and interactive but
  // deaf: CI caught an ArrowDown and an Escape landing in it. Only the
  // click listener needs deferring, and only because the click that
  // opened the panel is still bubbling toward the document, where
  // onOutsideClick would close it again immediately.
  document.addEventListener('keydown', onKey);
  setTimeout(() => document.addEventListener('click', onOutsideClick), 0);
}

/* Right-aligned to the anchor, clamped into the viewport. On a narrow
   screen the panel is full-width, so alignment is moot and the clamp is
   what keeps it on screen. */
function position(panel, anchor) {
  const rect = anchor.getBoundingClientRect();
  panel.style.top = `${rect.bottom + 8}px`;
  panel.style.right = `${Math.max(8, window.innerWidth - rect.right)}px`;
}

function focusables() {
  return openMenu ? [...openMenu.panel.querySelectorAll('a, button')] : [];
}

function onKey(event) {
  if (!openMenu) return;

  if (event.key === 'Escape') {
    // Focus goes back to the control that opened the panel, or it lands
    // on <body> and the next Tab restarts from the top of the page.
    const { anchor } = openMenu;
    closeMenu();
    anchor.focus();
    return;
  }

  const items = focusables();
  if (!items.length) return;
  // -1 while focus is still on the opener, which is the usual case for
  // the first arrow press.
  const at = items.indexOf(document.activeElement);

  if (event.key === 'ArrowDown') {
    event.preventDefault();
    items[at < 0 ? 0 : (at + 1) % items.length].focus();
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    items[at < 0 ? items.length - 1 : (at - 1 + items.length) % items.length].focus();
  } else if (event.key === 'Home') {
    event.preventDefault();
    items[0].focus();
  } else if (event.key === 'End') {
    event.preventDefault();
    items[items.length - 1].focus();
  }
}

function onOutsideClick(event) {
  if (openMenu && !openMenu.panel.contains(event.target)) closeMenu();
}

function closeMenu() {
  if (!openMenu) return;
  openMenu.panel.remove();
  openMenu.anchor.setAttribute('aria-expanded', 'false');
  openMenu = null;
  document.removeEventListener('click', onOutsideClick);
  document.removeEventListener('keydown', onKey);
}
